import { AxiosInstance } from 'axios';
import { LandingOffer, Referral, ReferralSearchDirectory } from 'referral-types';
import { addFormValues, simulate } from './utils';
import { type CompleteFormFields } from '../components/group-referral/form/group-referral.schema';
import { type FormFields as ReferralFormFields } from '../components/group-referral/group.schema';
import { Ratio, Size } from '../constants';
import { SITE_URL } from './service';

export const baseEndpoint = 'referral';

export class ReferralApi {
  private apiInstance: AxiosInstance;

  constructor(apiInstance: AxiosInstance) {
    this.apiInstance = apiInstance;
  }

  async getReferral(id: string): Promise<Referral> {
    await simulate();
    const response = await this.apiInstance.get(`/${id}`);

    return response.data;
  }

  async getLandingOffers(): Promise<LandingOffer[]> {
    await simulate();
    const response = await this.apiInstance.get(`/landing-offers`);

    return response.data;
  }

  async getReferrals(ids: string[]): Promise<Referral[]> {
    await simulate();
    const response = await this.apiInstance.post(`/batch`, { ids });

    return response.data;
  }

  async getSiteReferral(id: string): Promise<Referral> {
    await simulate();
    const response = await this.apiInstance.get(`/site/${id}`);

    return response.data;
  }

  async getReferralByGroupReferralId(groupReferralId: string): Promise<Referral> {
    await simulate();
    const response = await this.apiInstance.get(`/group-referral/${groupReferralId}`);

    return response.data;
  }

  getReferralImageSrc(imageId: string, size?: Size, ratio?: Ratio): string {
    const sizeVal = size || Size.md;
    const ratioVal = ratio || Ratio.rectangle;

    return `${SITE_URL}/image/referral/${imageId}?size=${sizeVal}&ratio=${ratioVal}`;
  }

  async searchReferrals(searchDirectory: ReferralSearchDirectory): Promise<Array<Referral>> {
    await simulate();
    const response = await this.apiInstance.post(`/search`, searchDirectory);

    return response.data;
  }

  async searchReferralsByLink(referralLink: string): Promise<Array<Referral>> {
    await simulate();
    const response = await this.apiInstance.post(`/search`, { referralLink });

    return response.data;
  }

  async updateReferral(
    referralId: string,
    formValues: Partial<CompleteFormFields>,
    autoPublish?: boolean
  ): Promise<Referral> {
    await simulate();
    const formData = new FormData();

    addFormValues(formValues, formData);

    if (formValues.expirationDateTime === '') {
      formData.delete('expirationDateTime');
    }

    const response = await this.apiInstance.put(`/${referralId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: autoPublish ? { autoPublish: true } : {},
    });

    return response.data;
  }

  async createFeaturedReferral(formValues: ReferralFormFields): Promise<Referral> {
    await simulate();
    const formData = new FormData();

    addFormValues({ ...formValues }, formData);

    if (formValues.expirationDateTime === '') {
      formData.delete('expirationDateTime');
    }

    const response = await this.apiInstance.post(`/featured`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return response.data;
  }
}
